import React from "react";
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  Button,
  TextField,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  fetchContentStudentLinks,
  setStudentContentLink,
  clearStudentContentLink,
} from "../api/backend";

/**
 * Lets staff set/update/clear each enrolled student's private external link
 * for a PER_STUDENT EXTERNAL_LINK content item. The value is never fetched
 * back once set - only a "set" status + timestamp is shown here.
 */
export default function ExternalLinkRosterModal({ open, item, onClose }) {
  const [roster, setRoster] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [editingId, setEditingId] = React.useState(null);
  const [draftUrl, setDraftUrl] = React.useState("");
  const [savingId, setSavingId] = React.useState(null);

  React.useEffect(() => {
    if (!open || !item) return;
    setLoading(true);
    fetchContentStudentLinks(item.id)
      .then((rows) => setRoster(Array.isArray(rows) ? rows : []))
      .catch(() => setRoster([]))
      .finally(() => setLoading(false));
  }, [open, item]);

  const startEdit = (studentUserId) => {
    setEditingId(studentUserId);
    setDraftUrl("");
  };

  const save = async (studentUserId) => {
    if (!draftUrl.trim()) return;
    setSavingId(studentUserId);
    try {
      await setStudentContentLink(item.id, studentUserId, draftUrl.trim());
      setRoster((prev) =>
        prev.map((r) =>
          r.studentUserId === studentUserId
            ? { ...r, hasLink: true, updatedAt: new Date().toISOString() }
            : r,
        ),
      );
      setEditingId(null);
      setDraftUrl("");
    } finally {
      setSavingId(null);
    }
  };

  const clear = async (studentUserId) => {
    setSavingId(studentUserId);
    try {
      await clearStudentContentLink(item.id, studentUserId);
      setRoster((prev) =>
        prev.map((r) =>
          r.studentUserId === studentUserId
            ? { ...r, hasLink: false, updatedAt: null }
            : r,
        ),
      );
    } finally {
      setSavingId(null);
    }
  };

  if (!item) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Typography sx={{ fontWeight: 700 }}>
            Manage student links — {item.title}
          </Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 2 }}>
          Each student's link is private once saved — you will not be able to
          view it again, only replace it.
        </Typography>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={22} />
          </Box>
        ) : (
          roster.map((r) => (
            <Box
              key={r.studentUserId}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                py: 1,
                borderBottom: "1px solid var(--color-hairline-cloud)",
              }}
            >
              <Typography sx={{ flex: 1, fontSize: 14 }}>
                {r.studentName || `Student #${r.studentUserId}`}
              </Typography>
              {editingId === r.studentUserId ? (
                <>
                  <TextField
                    size="small"
                    placeholder="https://..."
                    value={draftUrl}
                    onChange={(e) => setDraftUrl(e.target.value)}
                    sx={{ flex: 1 }}
                  />
                  <Button
                    size="small"
                    variant="contained"
                    disabled={savingId === r.studentUserId || !draftUrl.trim()}
                    onClick={() => save(r.studentUserId)}
                  >
                    Save
                  </Button>
                  <Button size="small" onClick={() => setEditingId(null)}>
                    Cancel
                  </Button>
                </>
              ) : (
                <>
                  {r.hasLink ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <CheckCircleIcon sx={{ fontSize: 15, color: "success.main" }} />
                      <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                        Set
                        {r.updatedAt
                          ? ` · updated ${new Date(r.updatedAt).toLocaleDateString()}`
                          : ""}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
                      Not set
                    </Typography>
                  )}
                  <Button size="small" onClick={() => startEdit(r.studentUserId)}>
                    {r.hasLink ? "Update" : "Set"}
                  </Button>
                  {r.hasLink && (
                    <Button
                      size="small"
                      color="error"
                      disabled={savingId === r.studentUserId}
                      onClick={() => clear(r.studentUserId)}
                    >
                      Clear
                    </Button>
                  )}
                </>
              )}
            </Box>
          ))
        )}
      </Box>
    </Dialog>
  );
}
